import { useSessionContext } from 'app/utils/supabase/useSessionContext'

/**
 * Centralized hook to determine if TanStack Query hooks should be enabled.
 * 
 * This ensures consistent authentication checking across all useQuery and useInfiniteQuery hooks.
 * 
 * @returns boolean - true if queries should be enabled, false otherwise
 * 
 * Query is enabled when:
 * - User exists (session?.user?.id is truthy)
 * - Access token exists (session?.access_token is truthy)
 * 
 * Query is disabled when:
 * - No session user (!session?.user?.id)
 * - No access token (!session?.access_token)
 * - Session is still loading
 */
export const useQueryEnabled = (): boolean => {
  const { session, isLoading } = useSessionContext()
  
  // Don't enable queries while session is still loading
  if (isLoading) {
    return false
  }
  
  // Enable only if both user and access token exist
  return !!(session?.user?.id && session?.access_token)
}

/**
 * Variant that allows external override while maintaining session check.
 * 
 * @param enabled - External enablement flag (defaults to true)
 * @returns boolean - true if both external flag and session check pass
 */
export const useQueryEnabledWithOverride = (enabled: boolean = true): boolean => {
  const isSessionEnabled = useQueryEnabled()
  
  return enabled && isSessionEnabled
}